import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppId, AppThunkAPIType } from "../../../../app/appTypes"
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../../http"
import { DocumentSection } from "../../documentsApi"
import { FormDefinition, FormFieldDef, FormFieldValue, FormSectionData } from "./formSchema"

/** What the designer sends; the server decides the version, the status and the published keys. */
export interface FormDefinitionSave {
    code: string
    name: string
    fields: FormFieldDef[]
}

/** The API returns the document id as a number and leaves out what it does not know. */
interface FormSectionDto extends Omit<FormSectionData, 'values'> {
    documentId: number
    values: Record<string, FormFieldValue>
}

export interface LoadedFormSection extends FormSectionData {
    documentId: string
}

const toFormSection = (dto: FormSectionDto): LoadedFormSection => ({ ...dto, documentId: String(dto.documentId) })

export const loadFormDefinitions = createAsyncThunk<FormDefinition[], void, AppThunkAPIType>(
    "form-definitions/load-all",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/form-definitions` }))
        if (getWithAuth.fulfilled.match(result)) {
            return result.payload as FormDefinition[]
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" })
        }
    })

export const loadFormDefinition = createAsyncThunk<FormDefinition, string, AppThunkAPIType>(
    "form-definitions/load",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/form-definitions/${id}` }))
        if (getWithAuth.fulfilled.match(result)) {
            return result.payload as FormDefinition
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" })
        }
    })

export const createFormDefinition = createAsyncThunk<FormDefinition, { definition: FormDefinitionSave, publish: boolean }, AppThunkAPIType>(
    "form-definitions/create",
    async (payload, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ url: `api/form-definitions`, payload }))
        if (postWithAuth.fulfilled.match(result)) {
            return result.payload as FormDefinition
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" })
        }
    })

/** `expectedVersion` is the version the admin started from; the server answers 409 if it moved on. */
export const updateFormDefinition = createAsyncThunk<FormDefinition, {
    id: string
    definition: FormDefinitionSave
    publish: boolean
    expectedVersion: number
}, AppThunkAPIType>(
    "form-definitions/update",
    async ({ id, ...payload }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({ url: `api/form-definitions/${id}`, payload }))
        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as FormDefinition
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" })
        }
    })

/** Archive or restore. Neither bumps the version: the fields did not change. */
export const setFormDefinitionStatus = createAsyncThunk<FormDefinition, { id: string, status: 'PUBLISHED' | 'ARCHIVED' }, AppThunkAPIType>(
    "form-definitions/set-status",
    async ({ id, status }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({ url: `api/form-definitions/${id}/status`, payload: { status } }))
        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as FormDefinition
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" })
        }
    })

/** Only a draft can be deleted: a published one may already be copied into documents. */
export const deleteFormDefinition = createAsyncThunk<string, string, AppThunkAPIType>(
    "form-definitions/delete",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({ url: `api/form-definitions/${id}` }))
        if (deleteWithAuth.fulfilled.match(result)) {
            return id
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" })
        }
    })

/** Every FORM section of a document, in one call: the picker needs them all to know what is used. */
export const loadDocumentForms = createAsyncThunk<LoadedFormSection[], AppId | number, AppThunkAPIType>(
    "document-forms/load",
    async (documentId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/document-forms/${documentId}` }))
        if (getWithAuth.fulfilled.match(result)) {
            return (result.payload as FormSectionDto[]).map(toFormSection)
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" })
        }
    })

/**
 * Creates the section and its copy of the definition in one server transaction, so there is never a
 * FORM section without its form.
 */
export const createFormSection = createAsyncThunk<{ section: DocumentSection, form: LoadedFormSection }, {
    documentId: AppId | number
    name: string
    parentSectionId: string | null
    definitionId: string
}, AppThunkAPIType>(
    "document-forms/create",
    async ({ documentId, ...payload }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({ url: `api/document-forms/${documentId}`, payload }))
        if (postWithAuth.fulfilled.match(result)) {
            const { section, form } = result.payload as { section: DocumentSection, form: FormSectionDto }
            return { section, form: toFormSection(form) }
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" })
        }
    })

/** Writes a single field; the server merges just that key, so concurrent editors of other fields are safe. */
export const setFormFieldValue = createAsyncThunk<LoadedFormSection, {
    documentId: AppId | number
    sectionId: string
    key: string
    value: FormFieldValue
}, AppThunkAPIType>(
    "document-forms/set-value",
    async ({ documentId, sectionId, key, value }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(putWithAuth({
            url: `api/document-forms/${documentId}/${sectionId}/values`,
            payload: { key, value },
        }))
        if (putWithAuth.fulfilled.match(result)) {
            return toFormSection(result.payload as FormSectionDto)
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" })
        }
    })
